import React, { useRef, useState } from 'react';
import { useMutation, useQuery } from 'react-query';
import AxiosUtil from '../../Utils/AxiosUtil';
import {
    ManagedActionModal,
    ManagedActionModalState,
    ManagedApiError,
    buildManagedActionModalState,
} from '../Shared/ManagedHelpers';
import type { ColumnsType } from 'antd/es/table';
import {
    AddIcon,
    Alert as ServaAlert,
    Button,
    Checkbox,
    Col,
    Confirmation,
    DatabaseOutlinedIcon,
    DeleteIcon,
    EditIcon,
    FileTextOutlinedIcon,
    Form,
    Input,
    Modal as ServaModal,
    Row,
    Space,
    Spin,
    Table,
    Text,
    Title,
    Tooltip,
    Card,
} from '@servahealth/serva-ui-kit';
import Administration from '../../Constants/Endpoints/Administration';
import { SimplifiedUser } from '../Shared/SimplifiedUser';
import EnableUserRequest from '../../Interfaces/Requests/Administration/EnableUserRequest';
import DisableUserRequest from '../../Interfaces/Requests/Administration/DisableUserRequest';
import GetUserAdminModelsRequest from '../../Interfaces/Requests/Administration/GetUserAdminModelsRequest';
import GetUserAdminModelsResponse from '../../Interfaces/Responses/Administration/GetUserAdminModelResponse';
import SSOEnabledCheckUserRequestModel from '../../Interfaces/Requests/Administration/SSOEnabledCheckUserRequestModel';
import SSOEnabledCheckUserResponseModel from '../../Interfaces/Responses/Administration/SSOEnabledCheckUserResponseModel';
import SSOEnabledCheckIcon from '../Icons/SSOEnabledCheckIcon';
import User from './LNKUser';
import UserReports from './LNKUserReports';
import UserTenants from './LNKUserTenants';
import UndeleteIcon from '../Icons/UndeleteIcon';

type AdminUserProps = {};

const AdminUser: React.FC<AdminUserProps> = () => {
    const [users, setUsers] = useState<SimplifiedUser[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<SimplifiedUser[]>([]);
    const [upsertUserModalOpen, setUpsertUserModalOpen] = useState(false);
    const [manageReportsModalOpen, setManageReportsModalOpen] = useState(false);
    const [manageTenantsModalOpen, setManageTenantsModalOpen] = useState(false);
    const [ssoEnabledCheckUserModalOpen, setSSOEnabledCheckUserModalOpen] = useState(false);
    const [currentSimplifiedUser, setCurrentSimplifiedUser] = useState<SimplifiedUser | null>();
    const [searchText, setSearchText] = useState('');
    const searchTextRef = useRef('');
    const [ssoEnabledCheckLocalUser, setSSOEnabledCheckLocalUser] = useState(''); 
    const [ssoEnabledCheckRemoteUser, setSSOEnabledCheckRemoteUser] = useState(''); 
    const [isShowDisabled, setIsShowDisabled] = useState(false);
    const [newUser, setNewUser] = useState(true);

    const [formAdminUser] = Form.useForm();

    const [currentError, setCurrentError] = useState('');

    const [manageActionModalState, setManageActionModalState] = useState<ManagedActionModalState>({
        open: false,
        mode: 'info',
        title: '',
        message: '',
        isError: false,
    });

    const showAdminUserInfoModal = (title: string, message: string, isError: boolean = true) => {
        setManageActionModalState(buildManagedActionModalState('info', title, message, isError));
    };

    const handleManageActionModalOk = () => {
        setManageActionModalState((currentState) => ({
            ...currentState,
            open: false,
        }));
    };

    const handleAdminUserFinishFailed = () => {
        showAdminUserInfoModal(
            'Manage Users Error',
            'The Manage Users form could not be submitted.  Review the form and try again.'
        );
    };

    const { isLoading: isGetUsersLoading, isError: isGetUsersError, error: getUsersError, refetch: refetchUsers } = useQuery(
        ['admin-users', isShowDisabled],
        async () => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.GET_USER_ADMIN_MODELS}`;
                const requestData: GetUserAdminModelsRequest = {
                    gua_includeDisabled: isShowDisabled,
                };
                return await AxiosUtil.get<GetUserAdminModelsResponse[], GetUserAdminModelsRequest>(url, requestData);
            } catch (err) {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get Admin Users] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get Admin Users] Internal Error: ', err);
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown) => {
                setCurrentError('GET_USER_ADMIN_MODELS: [Get Admin Users] Internal Error: ' + err);
                console.error('GET_USER_ADMIN_MODELS: [Get Admin Users] Internal Error: ', err);
            },
            onSuccess: (response: GetUserAdminModelsResponse[]) => {
                if (response) {
                    const currentSearchText = searchTextRef.current;

                    const simplifiedUsers: SimplifiedUser[] = response.map(
                        (eachElement) => {
                            return {
                                id: eachElement.id,
                                firstName: eachElement.firstName,
                                lastName: eachElement.lastName,
                                email: eachElement.email,
                                password: eachElement.password,
                                userName: eachElement.userName,
                                phone: eachElement.phone,
                                adminComment: eachElement.adminComment,
                                changePassword: eachElement.changePasswordRequired,
                                reportsOnly: eachElement.reportsOnly,
                                isSystemAccount: eachElement.isSystemAccount,
                                isDemo: eachElement.isDemo,
                                securityAdmin: eachElement.securityAdmin,
                                techTeam: eachElement.techTeam,
                                deleted: eachElement.deleted,
                            };
                        }
                    );

                    setUsers(simplifiedUsers);
                    setFilteredUsers(filterUsersBySearchText(simplifiedUsers, currentSearchText));
                }
            },
        }
    );

    const { mutate: disUser, isLoading: isDisableUserLoading, isError: isDisUserError, error: getDisUserError } = useMutation(
        async (aUser: SimplifiedUser) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.DISABLE_USER_REQUEST}`;
                const requestData: DisableUserRequest = {
                    id: aUser.id,
                    firstName: aUser.firstName,
                    lastName: aUser.lastName,
                    userName: aUser.userName,
                    password: aUser.password,
                };
                const responseData = await AxiosUtil.post<GetUserAdminModelsResponse, DisableUserRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('DISABLE_USER_REQUEST: [Disable User] Internal Error: ' + err);
                console.error('DISABLE_USER_REQUEST: [Disable User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'Disable User Error',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aUser: SimplifiedUser) => {
                setCurrentError('DISABLE_USER_REQUEST: [Disable User] Internal Error: ' + err);
                console.error('DISABLE_USER_REQUEST: [Disable User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'Disable User Error',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Unable To Be Disabled.  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetUserAdminModelsResponse, aUser: SimplifiedUser) => {
                await refetchUsers();

                showAdminUserInfoModal(
                    'Disable User',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Successfully Disabled.`,
                    false
                );
            },
        }
    );

    const { mutate: enaUser, isLoading: isEnableUserLoading, isError: isEnaUserError, error: getEnaUserError } = useMutation(
        async (aUser: SimplifiedUser) => {
            try {
                const url = `${process.env.REACT_APP_API_ROOT}${Administration.ENABLE_USER_REQUEST}`;
                const requestData: EnableUserRequest = {
                    id: aUser.id,
                    userName: aUser.userName,
                    email: aUser.email,
                    firstName: aUser.firstName,
                    lastName: aUser.lastName,
                    phone: aUser.phone,
                    adminComment: aUser.adminComment,
                    password: aUser.password,
                    isDemo: aUser.isDemo,
                    reportsOnly: aUser.reportsOnly,
                    isSystemAccount: aUser.isSystemAccount,
                    securityAdmin: aUser.securityAdmin,
                    techTeam: aUser.techTeam,
                };
                const responseData = await AxiosUtil.post<GetUserAdminModelsResponse, EnableUserRequest>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('ENABLE_USER_REQUEST: [Enable User] Internal Error: ' + err);
                console.error('ENABLE_USER_REQUEST: [Enable User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'Restore User Error',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aUser: SimplifiedUser) => {
                setCurrentError('ENABLE_USER_REQUEST: [Enable User] Internal Error: ' + err);
                console.error('ENABLE_USER_REQUEST: [Enable User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'Restore User Error',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Unable To Be Restored ([Re] Enabled.)  FAILED: Contact Application Support`
                );
            },
            onSuccess: async (response: GetUserAdminModelsResponse, aUser: SimplifiedUser) => {
                await refetchUsers();

                showAdminUserInfoModal(
                    'Restore User',
                    `User: ${aUser.firstName} ${aUser.lastName} Was Successfully Restored ([Re] Enabled.)`,
                    false
                );
            },
        }
    );

    const { mutate: ssoEnabledCheckUser, isLoading: isSSOEnabledCheckUserLoading, isError: isSSOEnabledCheckUserError, error: getSSOEnabledCheckUserError } = useMutation(
        async (aUser: SimplifiedUser) => {
            try {
                setSSOEnabledCheckLocalUser(aUser.email); 

                const url = `${process.env.REACT_APP_API_ROOT}${Administration.SSO_ENABLEDCHECK_USER_REQUEST}`;
                const requestData: SSOEnabledCheckUserRequestModel = {
                    email: aUser.email,
                };
                const responseData = await AxiosUtil.post<SSOEnabledCheckUserResponseModel, SSOEnabledCheckUserRequestModel>(url, requestData);
                return responseData;
            } catch (err) {
                setCurrentError('SSO_ENABLEDCHECK_USER_REQUEST: [SSO Enabled For User] Internal Error: ' + err);
                console.error('SSO_ENABLEDCHECK_USER_REQUEST: [SSO Enabled For User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'SSO Enabled Check Error',
                    'SSO Enabled Check Was Unable To Be Completed. Please Contact Application Support'
                );
                return Promise.reject(err);
            }
        },
        {
            onError: (err: unknown, aUser: SimplifiedUser) => {
                setCurrentError('SSO_ENABLEDCHECK_USER_REQUEST: [SSO Enabled For User] Internal Error: ' + err);
                console.error('SSO_ENABLEDCHECK_USER_REQUEST: [SSO Enabled For User] Internal Error: ', err);
                showAdminUserInfoModal(
                    'SSO Enabled Check Error',
                    'SSO Enabled Check Was Unable To Be Completed. Please Contact Application Support'
                );
            },
            onSuccess: (response: SSOEnabledCheckUserResponseModel, aUser: SimplifiedUser) => {
                setSSOEnabledCheckRemoteUser(response.email);
                setSSOEnabledCheckUserModalOpen(true);
            },
        }
    );

    const columns: ColumnsType<SimplifiedUser> = [
        { title: 'ID', dataIndex: 'id', key: 'id', width: 60, ellipsis: true,
            onCell: (record: SimplifiedUser) => ({
                style: { color: !record.deleted ? 'black' : 'red' },
            }),
            render: (text: number) => <div>{text}</div>,
        },
        { title: 'First Name', dataIndex: 'firstName', key: 'firstName' },
        { title: 'Last Name', dataIndex: 'lastName', key: 'lastName' },
        { title: 'User Name', dataIndex: 'userName', key: 'userName', ellipsis: true },
        { title: 'Email', dataIndex: 'email', key: 'email', ellipsis: true },
        { title: 'Phone', dataIndex: 'phone', key: 'phone', width: 115 },
        { title: 'Is Sys. Acct. ?', dataIndex: 'isSystemAccount', key: 'isSystemAccount', width: 115, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Is Demo Acct. ?', dataIndex: 'isDemo', key: 'isDemo', width: 115, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Is Sec. Acct. ?', dataIndex: 'securityAdmin', key: 'securityAdmin', width: 115, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Is Tech. Acct. ?', dataIndex: 'techTeam', key: 'techTeam', width: 115, align: 'center', render: (text: boolean) => (text ? <div>&#10004;</div> : <div></div>) },
        { title: 'Action', key: 'action', width: 130, fixed: 'right',
            render: (text: unknown, record: SimplifiedUser) => (
                <Space size={0}>
                    <Tooltip title='SSO Enabled Check'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<SSOEnabledCheckIcon />}
                            onClick={() => handleSSOEnabledCheck(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Edit User'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<EditIcon />}
                            onClick={() => handleEditUser(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Reports'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<FileTextOutlinedIcon />}
                            onClick={() => handleManageReports(record)}
                        />
                    </Tooltip>
                    <Tooltip title='Manage Tenants'>
                        <Button
                            size='small'
                            className='m-0 p-0'
                            type='link'
                            icon={<DatabaseOutlinedIcon />}
                            onClick={() => handleManageTenants(record)}
                        />
                    </Tooltip>
                    {
                        (!record.deleted ?
                            <Tooltip title='Disable User'>
                                <Confirmation
                                    title='Are you sure you want to disable this user?'
                                    onConfirm={() => disUser(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button 
                                        size='small'
                                        className='m-0 p-0'
                                        danger 
                                        type='link' 
                                        icon={<DeleteIcon />} 
                                    />
                                </Confirmation>
                            </Tooltip>
                            :
                            <Tooltip title='Restore ( [Re] Enable ) User'>
                                <Confirmation
                                    title='Are you sure you want to restore ( [re] enable ) this user?'
                                    onConfirm={() => enaUser(record)}
                                    okText='Yes'
                                    cancelText='No'
                                >
                                    <Button
                                        size='small'
                                        className='m-0 p-0'
                                        type='link'
                                        icon={<UndeleteIcon />}
                                    />
                                </Confirmation>
                            </Tooltip>
                        )
                    }
                </Space>
            ),
        },
    ];

    const handleIsShowDisabledChange = (aValue: { target: { checked: boolean } }) => {
        setIsShowDisabled(aValue.target.checked);
    };

    const handleEditUser = (aUser: SimplifiedUser) => {
        setNewUser(false);
        setCurrentSimplifiedUser(aUser);
        setUpsertUserModalOpen(true);
    };

    const handleManageReports = (aUser: SimplifiedUser) => {
        setManageReportsModalOpen(true);
        setCurrentSimplifiedUser(aUser);
    };

    const handleManageTenants = (aUser: SimplifiedUser) => {
        setManageTenantsModalOpen(true);
        setCurrentSimplifiedUser(aUser);
    };

    const handleSSOEnabledCheck = (aUser: SimplifiedUser) => {
        ssoEnabledCheckUser(aUser);
    };

    const handleCreateUser = () => {
        setNewUser(true);
        setCurrentSimplifiedUser(null);
        setUpsertUserModalOpen(true);
    };

    const filterUsersBySearchText = (userRows: SimplifiedUser[], value: string): SimplifiedUser[] => {
        const lowerCaseSearchText = value.trim().toLocaleLowerCase();

        if (lowerCaseSearchText === '') {
            return userRows;
        }

        return userRows.filter((eachUser) => {
            return (eachUser.firstName.toLowerCase().includes(lowerCaseSearchText) ||
                eachUser.lastName.toLowerCase().includes(lowerCaseSearchText) ||
                eachUser.email.toLowerCase().includes(lowerCaseSearchText)
            );
        });
    };

    const handleSearchTextChange = (aValue: React.ChangeEvent<HTMLInputElement>) => {
        const value = aValue.target.value;

        searchTextRef.current = value;
        setSearchText(value);
        setFilteredUsers(filterUsersBySearchText(users, value));
    };

    const doSearch = (sValue: string) => {
        searchTextRef.current = sValue;
        setSearchText(sValue);
        setFilteredUsers(filterUsersBySearchText(users, sValue));
    };

    const determineWhichError = () => {
        if ( isGetUsersError ) {
            return currentError + ' ' + ((getUsersError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isDisUserError ) {
            return currentError + ' ' + ((getDisUserError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isEnaUserError ) {
            return currentError + ' ' + ((getEnaUserError as ManagedApiError).response?.data?.message ?? '');
        }
        else if ( isSSOEnabledCheckUserError ) {
            return currentError + ' ' + ((getSSOEnabledCheckUserError as ManagedApiError).response?.data?.message ?? '');
        }
        else {
            return '';
        }
    };

    return (
        <Card className='shadow-card admin-full-page-card' size='small'>
            <ManagedActionModal
                modalState={manageActionModalState}
                onOk={handleManageActionModalOk}
                onCancel={handleManageActionModalOk}
            />
            <ServaModal
                hideButtons={true}
                open={upsertUserModalOpen}
                onCancel={() => setUpsertUserModalOpen(false)}
            >
                <User
                    id={currentSimplifiedUser?.id ?? 0}
                    firstName={currentSimplifiedUser?.firstName ?? ''}
                    lastName={currentSimplifiedUser?.lastName ?? ''}
                    email={currentSimplifiedUser?.email ?? ''}
                    password={currentSimplifiedUser?.password ?? ''}
                    userName={currentSimplifiedUser?.userName ?? ''}
                    phone={currentSimplifiedUser?.phone ?? ''}
                    adminComment={currentSimplifiedUser?.adminComment ?? ''}
                    changePassword={currentSimplifiedUser?.changePassword ?? false}
                    reportsOnly={currentSimplifiedUser?.reportsOnly ?? false}
                    isSystemAccount={currentSimplifiedUser?.isSystemAccount ?? false}
                    isDemo={currentSimplifiedUser?.isDemo ?? false}
                    securityAdmin={currentSimplifiedUser?.securityAdmin ?? false}
                    techTeam={currentSimplifiedUser?.techTeam ?? false}
                    newUser={newUser}
                    close={() => setUpsertUserModalOpen(false)}
                    refetch={() => refetchUsers()}
                ></User>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                open={manageReportsModalOpen}
                onCancel={() => setManageReportsModalOpen(false)}
                hideButtons={true}
                width='65%'
            >
                <UserReports
                    userId={currentSimplifiedUser?.id ?? 0}
                    userName={currentSimplifiedUser?.userName ?? ''}
                    close={() => setManageReportsModalOpen(false)}
                ></UserReports>
            </ServaModal>
            <ServaModal
                destroyOnHidden={true}
                open={manageTenantsModalOpen}
                onCancel={() => setManageTenantsModalOpen(false)}
                hideButtons={true}
                width='65%'
            >
                <UserTenants
                    userId={currentSimplifiedUser?.id ?? 0}
                    userName={currentSimplifiedUser?.userName ?? ''}
                    close={() => setManageTenantsModalOpen(false)}
                ></UserTenants>
            </ServaModal>
            <ServaModal
                open={ssoEnabledCheckUserModalOpen}
                onCancel={() => setSSOEnabledCheckUserModalOpen(false)}
                hideButtons={true}
                title={
                    <>
                        <Row>
                            <Col span={2}></Col>
                            <Col span={20}>
                                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                                    <Text style={{height: '100%', width: '100%'}}>
                                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>SSO Enabled Check</Title>
                                    </Text>
                                </Space>
                            </Col>
                            <Col span={2}></Col>
                        </Row>
                    </>
                }
            >
                <div style={{ width: '100%', overflowX: 'auto' }}>
                    <Row>
                        <Col span={7}>
                            <label style={{ float:'right'}}>For User ID:</label>
                        </Col>
                        <Col span={1} />
                        <Col span={16}>
                            <label style={{ fontWeight:'bold' }}>{ssoEnabledCheckLocalUser}</label>
                        </Col>
                    </Row>
                    <Row>
                        <Col span={7}>
                            <label style={{ float:'right'}}>SSO Check Result:</label>
                        </Col>
                        <Col span={1} />
                        <Col span={16}>
                            <label style={{color: ssoEnabledCheckRemoteUser !== '' ? 'green' : 'red'}}> {ssoEnabledCheckRemoteUser !== '' ? 'AN SSO MATCH WAS FOUND' : 'AN SSO MATCH WAS NOT FOUND'}</label>
                        </Col>
                    </Row>
                    <hr />
                    <Row>
                        <Col span={20}>
                        </Col>
                        <Col span={4}>
                            <Button type='primary' onClick={() => setSSOEnabledCheckUserModalOpen(false)}>OK</Button>
                        </Col>
                    </Row>
                </div>
            </ServaModal>
            <Spin spinning={
                            isGetUsersLoading ||
                            isDisableUserLoading ||
                            isEnableUserLoading ||
                            isSSOEnabledCheckUserLoading
                           }>
                <div style={{ display: 'table', margin: 'auto', textAlign: 'center', width: '50%' }}>
                {
                    (
                        isGetUsersError ||
                        isDisUserError ||
                        isEnaUserError ||
                        isSSOEnabledCheckUserError
                    )
                    &&
                    (
                        <ServaAlert
                            className={'mb-2'}
                            message={determineWhichError()}
                            showIcon={true}
                            type='error'
                        ></ServaAlert>
                    )
                }
                </div>
                <Space direction='horizontal' style={{justifyContent: 'center', padding: '8px', width: '100%'}}>
                    <Text style={{height: '100%', width: '100%'}}>
                        <Title style={{backgroundColor: 'rgb(00, 55, 90)', borderRadius: '10px', color: 'white', height: '100%', padding: '8px', width: '100%'}} level={4}>Manage Users And User Links To Tenants And Reports</Title>
                    </Text>
                </Space>
                <Form
                    form={formAdminUser}
                    initialValues={{
                        AuIsShowDisabled: false,
                    }}
                    key='adminuser'
                    onFinishFailed={handleAdminUserFinishFailed}
                >
                    <Row gutter={16} style={{ padding: '16px' }}>
                        <Col span={2}>
                            <Button
                                type='primary'
                                style={{
                                    marginBottom: '15px',
                                    float: 'left',
                                }}
                                icon={<AddIcon />}
                                onClick={handleCreateUser}
                            >
                                Create User
                            </Button>
                        </Col>
                        <Col span={2} />
                        <Col span={8}>
                            <Input
                                allowClear={true}
                                search={true}
                                style={{width: '75%'}}
                                value={searchText}
                                onChange={handleSearchTextChange}
                                onSearch={doSearch}
                            ></Input>
                        </Col>
                        <Col span={3} />
                        <Col span={3}>
                            <div
                                style={{
                                    height: '30px',
                                    paddingLeft: '5px'
                                }}
                            >
                                <Form.Item
                                    key='auisshowdisabled'
                                    name='AuIsShowDisabled'
                                    valuePropName='checked'
                                    style={{verticalAlign: 'middle'}}
                                >
                                    <Checkbox
                                        checked={isShowDisabled}
                                        style={{float: 'left', verticalAlign: 'middle'}}
                                        onChange={handleIsShowDisabledChange}
                                    >
                                        Show Disabled
                                    </Checkbox>
                                </Form.Item>
                            </div>
                        </Col>
                    </Row>
                    <Table
                        bordered
                        className='table-alternating-row-color'
                        columns={columns}
                        dataSource={filteredUsers}
                        rowClassName={() => 'rowClassName1'}
                        rowKey='id'
                        size='small'
                        style={{ padding: '12px' }}
                    />
                </Form>
            </Spin>
        </Card>
    );
};

export default AdminUser;
